import React, { useEffect, useState } from "react";
import { Loader2, Save, Eye, Sparkles, Layout as LayoutIcon, Info, Star, Store, Briefcase, Images, BarChart3, HelpCircle, MapPin } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import AdminLayout from "@/components/AdminLayout";
import { Button } from "@/components/ui/button";
import { useBranding } from "@/context/BrandingContext";
import { isSafeUrl } from "@/lib/companyProfile";
import { ModeEditor, HeroEditor, AboutEditor, WhyUsEditor, StorePreviewEditor, BusinessCtaEditor } from "@/components/admin/company/SectionEditors";
import { GalleryEditor, StatsEditor, FaqEditor, LocationEditor } from "@/components/admin/company/ListSectionEditors";

const TABS = [
  { key: "mode", label: "Mode & Urutan", icon: LayoutIcon },
  { key: "hero", label: "Hero", icon: Sparkles },
  { key: "about", label: "Tentang", icon: Info },
  { key: "why_us", label: "Keunggulan", icon: Star },
  { key: "store_preview", label: "Pratinjau Toko", icon: Store },
  { key: "business_cta", label: "Bisnis / Grosir", icon: Briefcase },
  { key: "gallery", label: "Galeri", icon: Images },
  { key: "stats", label: "Statistik", icon: BarChart3 },
  { key: "faq", label: "FAQ", icon: HelpCircle },
  { key: "location", label: "Lokasi & Kontak", icon: MapPin },
];

/* URL yang tidak valid mencegah simpan (backend juga menyanitasi → kosong). */
function invalidUrls(cfg) {
  const urls = [cfg.hero?.primary_cta?.url, cfg.hero?.secondary_cta?.url, cfg.business_cta?.cta_url, cfg.location?.maps_url];
  return urls.filter((u) => String(u || "").trim() && !isSafeUrl(u)).length;
}

/* Admin — Company Profile Phase 2: editor berbasis section (bukan satu form raksasa). Satu tombol Simpan = PUT dokumen penuh. */
export default function AdminCompanyProfile() {
  const [cfg, setCfg] = useState(null);
  const [tab, setTab] = useState("mode");
  const [saving, setSaving] = useState(false);
  const { branding } = useBranding();

  useEffect(() => {
    api.get("/admin/company-profile").then(({ data }) => setCfg(data)).catch((e) => toast.error(formatApiError(e)));
  }, []);

  const set = (patch) => setCfg((s) => ({ ...s, ...patch }));
  const setSection = (key) => (patch) => setCfg((s) => ({ ...s, [key]: { ...(s[key] || {}), ...patch } }));

  const save = async () => {
    if (invalidUrls(cfg) > 0) { toast.error("Ada tautan yang tidak valid. Periksa field URL yang ditandai merah."); return; }
    setSaving(true);
    try {
      const body = { ...cfg };
      ["mode", "meta", "updated_at"].forEach((k) => delete body[k]);
      const { data } = await api.put("/admin/company-profile", body);
      setCfg(data);
      toast.success(data.mode === "company_profile" ? "Tersimpan — mode Company Profile + Store aktif" : "Tersimpan — mode Store Only");
    } catch (e) { toast.error(formatApiError(e)); } finally { setSaving(false); }
  };

  const limits = cfg?.meta?.limits || {};
  const panel = cfg && {
    mode: <ModeEditor cfg={cfg} set={set} />,
    hero: <HeroEditor data={cfg.hero || {}} set={setSection("hero")} />,
    about: <AboutEditor data={cfg.about || {}} set={setSection("about")} />,
    why_us: <WhyUsEditor data={cfg.why_us || {}} set={setSection("why_us")} max={limits.why_us} />,
    store_preview: <StorePreviewEditor data={cfg.store_preview || {}} set={setSection("store_preview")} maxProducts={limits.products} />,
    business_cta: <BusinessCtaEditor data={cfg.business_cta || {}} set={setSection("business_cta")} />,
    gallery: <GalleryEditor data={cfg.gallery || {}} set={setSection("gallery")} max={limits.gallery} />,
    stats: <StatsEditor data={cfg.stats || {}} set={setSection("stats")} max={limits.stats} />,
    faq: <FaqEditor data={cfg.faq || {}} set={setSection("faq")} max={limits.faq} />,
    location: <LocationEditor data={cfg.location || {}} set={setSection("location")} contact={branding?.contact} />,
  }[tab];

  return (
    <AdminLayout title="Profil Perusahaan">
      <div className="space-y-4" data-testid="admin-company-profile">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="text-sm text-[#5C5C5C]">Susun halaman depan Company Profile per section. Perubahan disimpan sekaligus.</p>
          <div className="flex items-center gap-2">
            <a href="/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#5C5C5C] hover:text-black" data-testid="company-profile-preview-link"><Eye size={15} /> Lihat halaman</a>
            <Button onClick={save} disabled={saving || !cfg} data-testid="company-profile-save">
              {saving ? <Loader2 size={16} className="animate-spin mr-2" /> : <Save size={16} className="mr-2" />} Simpan
            </Button>
          </div>
        </div>
        {!cfg ? <div className="flex items-center gap-2 text-sm text-[#8A8A8A]"><Loader2 size={16} className="animate-spin" /> Memuat…</div> : (
          <div className="grid gap-4 lg:grid-cols-[240px_minmax(0,1fr)]">
            <nav aria-label="Section Company Profile" className="flex lg:flex-col gap-1 overflow-x-auto no-scrollbar lg:overflow-visible -mx-1 px-1 lg:sticky lg:top-4 lg:self-start" data-testid="cp-section-tabs">
              {TABS.map(({ key, label, icon: Icon }) => {
                const active = tab === key;
                const hidden = key !== "mode" && key !== "hero" && cfg[key] && cfg[key].enabled === false;
                return (
                  <button key={key} type="button" onClick={() => setTab(key)} aria-current={active ? "page" : undefined} data-testid={`cp-tab-${key}`}
                    className={`shrink-0 inline-flex items-center gap-2 rounded-xl px-3 py-2.5 text-sm font-semibold text-left transition-colors ${active ? "bg-[#111827] text-white" : "bg-white border border-[#E5E5E5] text-[#374151] hover:bg-[#F3F4F6]"}`}>
                    <Icon size={15} className="shrink-0" /> <span className="whitespace-nowrap">{label}</span>
                    {hidden ? <span className={`ml-auto text-[9px] font-bold uppercase tracking-wider ${active ? "text-white/70" : "text-[#9CA3AF]"}`}>off</span> : null}
                  </button>
                );
              })}
            </nav>
            <div className="min-w-0" data-testid={`cp-panel-${tab}`}>{panel}</div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
